/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-expressions */
/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
    FC, useCallback, useEffect, useMemo,
    useState,
} from 'react';
import {
    Button, Icon, Infobox, Loader, Modal, Select, Title,
} from 'ui-library';
import {
    ICON_EDIT_MINIMAL, ICON_EYE, ICON_TYPE_BIN, ICON_TYPE_CLOSE_SLIM,
} from 'ui-library/components/Icon';
import { THREEBTYPES } from 'constants/constants';
import arrayContains from 'utils/arrayUtils';
import { StaticField } from '../../../ui-library/components/StaticField/StaticField';
import { ExistingProductsRowProps } from './ShoppingCartProductRow.types';
import useExistingProducts, { CaseInvestmentPlan } from './useExistingProducts';
import WithdrawalPlanComp from './WithdrawalPlanComp';
import useCallbackState from '../../../hooks/useCallbackState';
import ChangeInvestmentModal from '../ChangeInvestmentModal/ChangeInvestmentModal';
import './ShoppingCartProductRow.css';

const ExistingProductRow: FC<ExistingProductsRowProps> = (props) => {
    const {
        product: portfolio,
        advisoryDocumentId,
        t, language, getAmountWithDecimals,
        saveInvestmentApplicationDataToStoreAction,
        removeInvestmentApplicationDataAction,
        optionPreferenceStock,
        optionPreferenceExclusion,
        optionPreferenceInclusion,
        advisoryId,
        canCheckInvestorProfile,
        checkInvestorProfileActive,
        handleCheckInvestorProfile,
        fakeLoading,
    } = props;

    const {
        modalStatus,
        setModalStatus,
        showEditIcon,
        isAzpOrAzpOld,
        handleEditStrategy,
        strategy,
        setStrategy,
        onChangeStrategy,
        modelPortfoliosLoading,
        strategyOptions,
        investment,
        onInvestmentPlanUpdate,
        NO_EXCLUSION,
        baseStrategyOptions,
        selectedStrategyIdState,
        setSelectedStrategyIdState,
        preferenceStockState,
        setPreferenceStockState,
        isCapital,
        preferenceInclusion,
        setPreferenceInclusionState,
        preferenceExclusionState,
        setPreferenceExclusionState,
        optionExclusionSubstitute,
        exclusionSubstituteState,
        setExclusionSubstituteState,
        isZIC,
        isPK,
        selectedProduct,
        selectedModelPortfolio,
        handleDiscard3bApplication,
        changeExisting3bPlanResponseIsPending,
        isWithdrawalSelected,
        setIsWithdrawalSelected,
        continueDisabled,
        withdrawalModalOpen,
        setWithdrawalModalOpen,
        withdrawalChangePlanData,
    } = useExistingProducts({
        portfolio,
        language,
        t,
        advisoryDocumentId,
        saveInvestmentApplicationDataToStoreAction,
        removeInvestmentApplicationDataAction,
        optionPreferenceStock,
        optionPreferenceExclusion,
        optionPreferenceInclusion,
    });

    const handleChangeStrategy = useCallback((data) => {
        const { onContinue } = withdrawalChangePlanData || {};

        if (withdrawalModalOpen && onContinue) {
            onContinue();
        } else {
            onChangeStrategy(data);
        }
    }, [withdrawalChangePlanData, withdrawalModalOpen]);

    useEffect(() => {
        const { setData } = withdrawalChangePlanData || {};

        if (setData && !modalStatus?.open) {
            setData();
        }
    }, [modalStatus?.open]);

    const [investmentPlan, setInvestmentPlan] = useCallbackState<CaseInvestmentPlan>({ isSell: false, amount: 0, isCaseSource: false });
    const invalidInvestmentAmount = useMemo(() => !(investmentPlan.isSell && investmentPlan.amount > +(portfolio?.amount as number)), [investmentPlan, portfolio?.amount]);

    const onBuySellToggleChange = useCallback((checked: boolean) => { setInvestmentPlan(prev => ({ ...prev, isSell: checked })); }, []);
    const onInvestmentAmountChanged = useCallback((value: string) => { setInvestmentPlan(prev => ({ ...prev, amount: +value })); }, []);
    const onFillOutPlanChanged = useCallback((e) => { setInvestmentPlan(prev => ({ ...prev, isCaseSource: JSON.parse(e.target.value) })); }, []);

    const setInvestementPlanValues = () => {
        if (investment) {
            setInvestmentPlan(prev => ({
                ...prev, isSell: investment.isSell, amount: investment.amount, isCaseSource: investment.isCaseSource,
            }));
        }
    };

    useEffect(() => { setInvestementPlanValues(); }, [investment, portfolio]);

    const onCancelInvestementPlan = () => {
        setInvestementPlanValues();
        setModalStatus({ open: false });
    };

    const onAcceptInvestmentPlan = () => {
        onInvestmentPlanUpdate(investmentPlan);
    };

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    const azpRadioGroupOptions = useMemo(() => [
        {
            label: t('CaseManagementPage.Modal.AZPModal.Sell.radioYes'),
            value: 'false',
        },
        {
            label: t('CaseManagementPage.Modal.AZPModal.Sell.radioNo'),
            value: 'true',
        },
    ], [t]);

    const portfolioAmount = useMemo(() => getAmountWithDecimals(portfolio?.amount), [portfolio, getAmountWithDecimals]);
    const investmentPlanAmount = useMemo(() => investmentPlan.amount, [investmentPlan]);
    const investmentPlanIsCaseSource = useMemo(() => investmentPlan.isCaseSource, [investmentPlan]);
    const investmentModalResources = useMemo(() => ({
        modalClass: 'change-strategy-modal',
        title: t('ShoppingCart.Modal.Form.Label.ChangeInvestmentAmount'),
        portfolioAmountLabel: t('ShoppingCart.Modal.Form.Label.InvestedAmount'),
        okText: t('confirmation.Save'),
        cancelText: t('confirmation.cancel'),
        paragraph1: t('CaseManagementPage.Modal.Paragraph1'),
        paragraph3: t('CaseManagementPage.Modal.Paragraph3'),
        amountErrorLabel: t('ShoppingCart.InvestmentAmount.ToHighError'),
        toggleUncheckedLabel: t('ShoppingCart.BuySellToggle.Label.Buy'),
        toggleCheckedLabel: t('ShoppingCart.BuySellToggle.Label.Sell'),
    }), [t]);

    const showCheckInvestorProfileButton = useMemo(
        () => arrayContains([THREEBTYPES.zivv, THREEBTYPES.azpAlt, THREEBTYPES.azpNeu, THREEBTYPES.azpNeuSB], portfolio?.existingProduct?.externalId)
        && !!advisoryId
        && canCheckInvestorProfile
        && !portfolio?.pendingCEPRequest,
        [
            advisoryId,
            portfolio?.existingProduct?.externalId,
            canCheckInvestorProfile,
            portfolio?.pendingCEPRequest,
        ],
    );

    const [checkInvestorProfileModalVisible, setCheckInvestorProfileModalVisible] = useState(false);

    useEffect(() => {
        if (checkInvestorProfileActive) {
            setCheckInvestorProfileModalVisible(false);
        }
    }, [checkInvestorProfileActive]);

    return (
        <>
            <div className="product-row existing-product-row">
                <div className="product-col">
                    <div className="configured-col">
                        <StaticField
                            className="product_name"
                            label={t('ShoppingCart.ProductRow.ProductName')}
                            value={portfolio?.existingProduct?.name as string}
                        />

                        <div className="product_strategy">
                            <StaticField
                                label={t('ShoppingCart.ProductRow.Strategy')}
                                value={portfolio?.newStrategy as string}
                                Icon={(showEditIcon && portfolio?.existingProduct?.externalId !== THREEBTYPES.zic) && (
                                    <Button
                                        type="link"
                                        onClick={() => handleEditStrategy({
                                            open: true,
                                            product: portfolio?.existingProduct?.externalId as string,
                                            field: 'strategy',
                                        })}
                                        className="edit-icon-button"
                                        disabled={checkInvestorProfileActive}
                                    >
                                        <Icon
                                            type={ICON_EDIT_MINIMAL}
                                            size={20}
                                        />
                                    </Button>
                                )}
                            />

                            {portfolio?.changedStrategy && (
                                <div className="total-amount-new">
                                    <span>
                                        {t('ShoppingCart.ProductRow.CurrentStrategy')}
                                        {': '}
                                        {portfolio?.oldStrategy}
                                    </span>
                                </div>
                            )}
                        </div>
                        <div className="product_investment">
                            <StaticField
                                label={t('ShoppingCart.ProductRow.Investment')}
                                value={`${portfolio?.changedAmount ? (portfolio?.isBuyTransaction) ? '+' : '-' : ''} ${getAmountWithDecimals(portfolio?.changedAmount ? Math.abs(portfolio?.changedAmount) : portfolio?.amount)}`}
                                Icon={(showEditIcon || isAzpOrAzpOld || isPK) && (
                                    <Button
                                        type="link"
                                        onClick={() => setModalStatus({
                                            open: true,
                                            product: portfolio?.existingProduct?.externalId as string,
                                            field: 'investment',
                                        })}
                                        className="edit-icon-button"
                                        disabled={checkInvestorProfileActive}
                                    >
                                        <Icon
                                            type={ICON_EDIT_MINIMAL}
                                            size={20}
                                        />
                                    </Button>
                                )}
                            />

                            {portfolio?.changedAmount != null && !checkInvestorProfileActive ? (
                                <div className="total-amount-new">
                                    <span>
                                        {t('ShoppingCart.ProductRow.CurrentAmount')}
                                        {': '}
                                        {getAmountWithDecimals(portfolio?.amount)}
                                    </span>
                                </div>
                            ) : ''}
                        </div>
                        {(portfolio?.editable && portfolio?.pendingCEPRequest)
                            && (
                                <Infobox className="shopping-cart-pending-cep-info">
                                    {t('ShoppingCart.ProductRow.LockedProductMessage')}
                                </Infobox>
                            )}
                        {portfolio?.edited && (
                            <Button
                                type="link"
                                onClick={handleDiscard3bApplication}
                            >
                                <Icon
                                    type={ICON_TYPE_BIN}
                                    size={20}
                                />
                                {' '}
                                {t('ShoppingCart.ProductRow.DiscardChanges')}
                            </Button>
                        )}
                        {showCheckInvestorProfileButton && (
                            <Button
                                type="link"
                                onClick={() => setCheckInvestorProfileModalVisible(true)}
                                disabled={checkInvestorProfileActive}
                            >
                                <Icon
                                    type={ICON_EYE}
                                    size={20}
                                />
                                <span className="text-truncate">{t('CheckInvestorProfile.btn')}</span>
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {modalStatus.open && modalStatus?.product !== THREEBTYPES.zivv && modalStatus?.field === 'strategy' && (
                <Modal
                    width={610}
                    visible
                    onCancel={() => setModalStatus({
                        open: false,
                    })}
                    onOk={onChangeStrategy}
                    okText={t('confirmation.Save')}
                    cancelText={t('confirmation.cancel')}
                    closable
                    borderRadius
                    okButtonProps={{
                        disabled: !strategy || (strategy === portfolio?.modelPortfolioId),
                        loading: changeExisting3bPlanResponseIsPending,
                    }}
                    closeIcon={closeIcon}
                    className="change-strategy-modal"
                >
                    <Title type={2} className="mb-24">
                        {portfolio?.existingProduct?.externalId}
                        {' - '}
                        {t('ShoppingCart.Modal.Form.Label.ChangeStrategy')}
                    </Title>
                    <div className="edit-strategy-modal--current-strategy-info mb-24">
                        {t('ShoppingCart.Modal.Form.Label.CurrentStrategy')}
                        {' '}
                        {portfolio?.oldStrategy}
                    </div>

                    <div className="edit-strategy-form">
                        {modelPortfoliosLoading ? <div style={{ margin: 24 }}><Loader /></div> : (
                            <Select
                                label={t('ShoppingCart.Modal.Form.Label.BaseStrategy')}
                                options={baseStrategyOptions}
                                placeholder={t('position.pleaseSelect')}
                                data-cy="change-strategy"
                                value={selectedStrategyIdState}
                                required
                                onChange={(val: number) => {
                                    setSelectedStrategyIdState(val);
                                    setStrategy(null);
                                }}
                            />
                        )}
                        {modelPortfoliosLoading ? <Loader />
                            : (
                                <Select
                                    label={t('ShoppingCart.Modal.Form.Label.NewStrategy')}
                                    options={strategyOptions}
                                    placeholder={strategy === undefined ? '-' : t('position.pleaseSelect')}
                                    data-cy="change-strategy"
                                    value={strategy}
                                    required
                                    onChange={setStrategy}
                                />
                            )}
                    </div>
                </Modal>
            )}

            <ChangeInvestmentModal
                visible={modalStatus.open && modalStatus?.field === 'investment'}
                closable
                hasBorderRadius
                width={610}
                closeIcon={closeIcon}
                okButtonProps={{
                    disabled: !investmentPlanAmount || !invalidInvestmentAmount,
                    loading: changeExisting3bPlanResponseIsPending || false,
                }}
                toggleChecked={investmentPlan.isSell}
                toggleError={null}
                toggleDisabled={isZIC || isPK}
                portfolioAmount={portfolioAmount}
                amount={investmentPlanAmount}
                amountIsValid={!invalidInvestmentAmount}
                isCaseSource={investmentPlanIsCaseSource}
                isAzpOrAzpOld={isAzpOrAzpOld}
                azpRadioGroupOptions={azpRadioGroupOptions}
                resources={investmentModalResources}
                // Event handlers
                onOk={onAcceptInvestmentPlan}
                onCancel={onCancelInvestementPlan}
                onToggleChange={onBuySellToggleChange}
                onAmountChanged={onInvestmentAmountChanged}
                onAzpRadioChange={onFillOutPlanChanged}
            />

            {modalStatus.open && modalStatus?.product === THREEBTYPES.zivv && modalStatus?.field === 'strategy' && (
                <Modal
                    width={withdrawalModalOpen ? 730 : 610}
                    visible
                    onCancel={(isWithdrawalSelected && withdrawalModalOpen) ? () => setWithdrawalModalOpen(false) : () => {
                        setModalStatus({
                            open: false,
                        });
                        setWithdrawalModalOpen(false);
                        setIsWithdrawalSelected(!!portfolio?.withdrawalAmount);
                    }}
                    onOk={(isWithdrawalSelected && !withdrawalModalOpen) ? () => setWithdrawalModalOpen(true) : handleChangeStrategy}
                    okText={isWithdrawalSelected && !withdrawalModalOpen ? t('confirmation.continue') : t('confirmation.Save')}
                    cancelText={withdrawalModalOpen ? t('confirmation.back') : t('confirmation.cancel')}
                    okButtonProps={{ loading: changeExisting3bPlanResponseIsPending || false, disabled: continueDisabled }}
                    closable
                    borderRadius
                    closeIcon={closeIcon}
                    className="change-strategy-modal"
                >

                    {!withdrawalModalOpen ? (
                        <>
                            <Title type={2} className="mb-24">
                                {t('ShoppingCart.Modal.Form.Label.ChangeZIVVStrategy')}
                            </Title>

                            <div className="edit-strategy-modal--current-strategy-info mb-24">
                                {t('ShoppingCart.Modal.Form.Label.CurrentStrategy')}
                                {' '}
                                {selectedModelPortfolio?.Name}
                            </div>

                            <div className="shopping-cart--withdrawal-option-buttons">
                                <Title type={3}>{t('ShoppingCart.Modal.Form.SetupWithdrawalPlan.labels.title')}</Title>
                                {/* <Title type={3}>Would you like to setup a withdrawal plan?</Title> */}
                                <div className="shopping-cart--withdrawal-option-buttons--wrapper">
                                    <Button
                                        onClick={() => setIsWithdrawalSelected(true)}
                                        type={isWithdrawalSelected ? 'tertiary' : 'tertiary-outlined'}
                                        size="small"
                                    >
                                        {t('setupWithdrawalPlan.buttons.yes')}
                                    </Button>
                                    <Button
                                        onClick={() => setIsWithdrawalSelected(false)}
                                        type={!isWithdrawalSelected ? 'tertiary' : 'tertiary-outlined'}
                                        size="small"
                                    >
                                        {t('setupWithdrawalPlan.buttons.no')}
                                    </Button>
                                </div>
                            </div>

                            <form className="edit-strategy-form">
                                {modelPortfoliosLoading ? <Loader /> : (
                                    <Select
                                        label={t('ShoppingCart.Modal.Form.Label.BaseStrategy')}
                                        options={baseStrategyOptions}
                                        placeholder={t('position.pleaseSelect')}
                                        data-cy="change-strategy"
                                        value={selectedStrategyIdState}
                                        required
                                        onChange={setSelectedStrategyIdState}
                                    />
                                )}
                                <Select
                                    label={t('ShoppingCart.Modal.Form.Label.PreferenceStocks')}
                                    options={optionPreferenceStock}
                                    placeholder={t('position.pleaseSelect')}
                                    data-cy="preference-stocks"
                                    required
                                    value={preferenceStockState?.toString() || null}
                                    onChange={setPreferenceStockState}
                                />
                                {isCapital ? (
                                    <Select
                                        data-cy="preference-inclusion"
                                        value={preferenceInclusion}
                                        options={optionPreferenceInclusion || []}
                                        label={t('portfolio.createNewPortfolio.selectStrategy.preferenceInclusion')}
                                        placeholder={t('portfolio.createNewPortfolio.agentInformation.selectPlaceholder')}
                                        onChange={setPreferenceInclusionState}
                                    />
                                ) : (
                                    <>
                                        <Select
                                            label={t('ShoppingCart.Modal.Form.Label.PreferenceExclusion')}
                                            options={optionPreferenceExclusion}
                                            placeholder={t('position.pleaseSelect')}
                                            data-cy="preference-execution"
                                            required
                                            value={preferenceExclusionState?.toString() || null}
                                            onChange={setPreferenceExclusionState}
                                        />
                                        <Select
                                            label={t('ShoppingCart.Modal.Form.Label.SubstituteForExclusion')}
                                            options={optionExclusionSubstitute}
                                            placeholder={t('position.pleaseSelect')}
                                            data-cy="substitute-for-execution"
                                            value={exclusionSubstituteState?.toString() || null}
                                            onChange={setExclusionSubstituteState}
                                            disabled={preferenceExclusionState && preferenceExclusionState === NO_EXCLUSION}
                                        />
                                    </>
                                )}

                                <div className="configured-col">
                                    <Title type={4}>
                                        <strong>
                                            {t('ShoppingCart.Modal.Form.Label.TargetStrategy')}
                                            :
                                        </strong>
                                        <div className="mt-12">
                                            {selectedProduct?.Name}
                                        </div>
                                    </Title>
                                </div>

                                <div className="target-strategy-note">
                                    {t('ShoppingCart.Modal.Form.Label.ChangeZIVVStrategy.Note')}
                                </div>
                            </form>
                        </>
                    ) : (
                        <>
                            <Title type={2} className="mb-24">
                                {t('ShoppingCart.Modal.Form.WithdrawalPlanFormTitle')}
                                {/* Withdrawal plan */}
                            </Title>
                            <WithdrawalPlanComp {...withdrawalChangePlanData} />
                        </>
                    )}
                </Modal>
            )}

            {checkInvestorProfileModalVisible && (
                <Modal
                    width={610}
                    visible
                    onCancel={() => setCheckInvestorProfileModalVisible(false)}
                    onOk={() => handleCheckInvestorProfile({ portfolioId: portfolio?.id as number })}
                    okText={t('confirmation.continue')}
                    cancelText={t('confirmation.cancel')}
                    closable
                    borderRadius
                    closeIcon={closeIcon}
                    className="check-investor-profile-modal change-strategy-modal"
                    okType="primary"
                    okButtonProps={{
                        disabled: fakeLoading,
                        loading: fakeLoading || false,
                    }}
                >
                    <Title type={2} className="mb-24">
                        {t('ShoppingCart.Modal.CheckInvestorProfile.Title')}
                    </Title>
                    <div className="check-investor-profile-modal-paragraph mb-16">
                        <span>
                            {t('ShoppingCart.Modal.CheckInvestorProfile.Para.One')}
                        </span>
                        <span>
                            {t('ShoppingCart.Modal.CheckInvestorProfile.Para.Two')}
                        </span>
                        <span>
                            {t('ShoppingCart.Modal.CheckInvestorProfile.Para.Three')}
                        </span>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default ExistingProductRow;
